/**
 * Injected into blog HTML via `BLOG_DECISION_FRAMEWORK_MARKER` for posts that opt in.
 * @see BlogPostContent
 */

import type { IConversionCtaAction } from './blog-conversion-cta';
import { buildTindloDemoMailtoHref, TINDLO_HELLO_EMAIL } from './tindlo-demo-mailto';

export const BLOG_DECISION_FRAMEWORK_MARKER = '<!--blog-mount:decision-framework-->';

export interface IDecisionFrameworkRow {
  id: string;
  problem: string;
  /** Tool name recommendation (informational — not a link) */
  bestFit: string;
  /** Subtle accent for recommended row — e.g. Tindlo on comparison flagship */
  emphasize?: boolean;
}

export interface IDecisionFrameworkConfig {
  rows: IDecisionFrameworkRow[];
  /** Optional CTA aligned with funnel styles (pricing / demo / trial) */
  midSectionCta?: {
    lead: string;
    actions: IConversionCtaAction[];
  };
}

const TINDLO_VS_COMPETITORS_2026: IDecisionFrameworkConfig = {
  rows: [
    {
      id: 'visibility',
      problem: 'Need to instantly see where your team stands right now',
      bestFit: 'Tindlo',
      emphasize: true,
    },
    {
      id: 'structure',
      problem: 'Need structured tracking across departments',
      bestFit: 'Asana',
    },
    {
      id: 'knowledge',
      problem: 'Need a flexible knowledge base and wiki',
      bestFit: 'Notion',
    },
    {
      id: 'automation',
      problem: 'Need workflow automation for repeatable processes',
      bestFit: 'Monday.com',
    },
    {
      id: 'all-in-one',
      problem: 'Want every feature in one platform',
      bestFit: 'ClickUp',
    },
  ],
  midSectionCta: {
    lead: 'Still deciding? Explore your next step.',
    actions: [
      {
        label: 'Start Free Trial',
        href: 'https://app.tindlo.com/',
        variant: 'primary',
        ctaAnalyticsName: 'start_free_trial',
      },
      /** Tindlo site `/pricing` (plans & billing), not a competitor-vs-price comparison */
      {
        label: 'View Tindlo pricing',
        href: '/pricing',
        variant: 'secondary',
        ctaAnalyticsName: 'compare_pricing',
      },
      /** 라벨은 짧게; 주소는 팝오버에서 복사/메일 앱 */
      {
        label: 'Product & demos',
        href: buildTindloDemoMailtoHref(),
        variant: 'secondary',
        ctaAnalyticsName: 'email_demo',
        ctaEmailPopover: { email: TINDLO_HELLO_EMAIL },
      },
    ],
  },
};

const BEST_TEAM_SCHEDULING_2026: IDecisionFrameworkConfig = {
  rows: [
    {
      id: 'timeline-docs',
      problem: 'Need schedules, tasks, and docs in one timeline',
      bestFit: 'Tindlo',
      emphasize: true,
    },
    {
      id: 'external-meetings',
      problem: 'Need external meeting booking',
      bestFit: 'Calendly',
    },
    {
      id: 'simple-calendar',
      problem: 'Need a simple personal/work calendar',
      bestFit: 'Google Calendar',
    },
    {
      id: 'visual-availability',
      problem: 'Need visual team availability',
      bestFit: 'Teamup',
    },
    {
      id: 'ai-auto-scheduling',
      problem: 'Want automatic AI scheduling',
      bestFit: 'Motion',
    },
  ],
  midSectionCta: {
    lead: 'Still scheduling work across too many tools?',
    actions: [
      {
        label: 'Start Free Trial',
        href: 'https://app.tindlo.com/',
        variant: 'primary',
        ctaAnalyticsName: 'start_free_trial',
      },
      {
        label: 'Compare Pricing',
        href: '/pricing',
        variant: 'secondary',
        ctaAnalyticsName: 'compare_pricing',
      },
      {
        label: 'Email us for a demo',
        href: buildTindloDemoMailtoHref(),
        variant: 'secondary',
        ctaAnalyticsName: 'email_demo',
        ctaEmailPopover: { email: TINDLO_HELLO_EMAIL },
      },
    ],
  },
};

const NOTION_ALTERNATIVES_PROJECT_MANAGEMENT: IDecisionFrameworkConfig = {
  rows: [
    {
      id: 'shipped-work',
      problem: "Plans don't turn into shipped work",
      bestFit: 'Tindlo',
      emphasize: true,
    },
    {
      id: 'task-ownership',
      problem: 'Need clearer task ownership',
      bestFit: 'Asana / ClickUp',
    },
    {
      id: 'tool-switching',
      problem: 'Too many tools, too much switching',
      bestFit: 'Tindlo',
    },
    {
      id: 'docs-priority',
      problem: 'Docs and knowledge are the main priority',
      bestFit: 'Notion',
    },
  ],
};

const BY_SLUG: Record<string, IDecisionFrameworkConfig> = {
  'tindlo-vs-competitors-2026': TINDLO_VS_COMPETITORS_2026,
  'best-team-scheduling-software-2026': BEST_TEAM_SCHEDULING_2026,
  'notion-alternatives-project-management': NOTION_ALTERNATIVES_PROJECT_MANAGEMENT,
};

export function getDecisionFrameworkBySlug(slug: string): IDecisionFrameworkConfig | null {
  return BY_SLUG[slug] ?? null;
}
